import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization, useUser } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

interface OrgMember {
  id: string;
  role: string;
  joinedAt: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
}

interface OrgDetails {
  id: string;
  name: string;
  seatCount: number;
  maxSeats: number;
}

export function OrgMembers() {
  const { organization } = useOrganization();
  const { user } = useUser();
  const { get, post, patch, delete: deleteApi } = useApi();

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [orgDetails, setOrgDetails] = useState<OrgDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviting, setInviting] = useState(false);

  // Current user's role
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  useEffect(() => {
    if (organization) {
      fetchData();
    }
  }, [organization?.id]);

  async function fetchData() {
    if (!organization) return;

    try {
      setLoading(true);
      setError(null);

      const [membersResponse, invitationsResponse, orgResponse] = await Promise.all([
        get<{ members: OrgMember[] }>(`/organizations/${organization.id}/members`),
        get<{ invitations: Invitation[] }>(`/organizations/${organization.id}/invitations`).catch(() => ({ invitations: [] })),
        get<OrgDetails>(`/organizations/${organization.id}`),
      ]);

      setMembers(membersResponse.members);
      setInvitations(invitationsResponse.invitations);
      setOrgDetails(orgResponse);

      // Find current user's role
      const currentMember = membersResponse.members.find(
        (m) => m.email === user?.primaryEmailAddress?.emailAddress
      );
      setCurrentUserRole(currentMember?.role || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load organization members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim() || !organization) return;

    try {
      setInviting(true);
      const invitation = await post<Invitation>(`/organizations/${organization.id}/invitations`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInvitations([invitation, ...invitations]);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      // Update seat info
      if (orgDetails) {
        setOrgDetails({ ...orgDetails, seatCount: orgDetails.seatCount + 1 });
      }
    } catch (err: any) {
      alert(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  }

  async function handleRevokeInvitation(invitationId: string) {
    if (!organization) return;
    if (!confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      await deleteApi(`/organizations/${organization.id}/invitations/${invitationId}`);
      setInvitations(invitations.filter((i) => i.id !== invitationId));
    } catch (err: any) {
      alert(err.message || 'Failed to revoke invitation');
    }
  }

  async function handleResendInvitation(invitationId: string) {
    if (!organization) return;

    try {
      await post(`/organizations/${organization.id}/invitations/${invitationId}/resend`, {});
      alert('Invitation resent successfully');
    } catch (err: any) {
      alert(err.message || 'Failed to resend invitation');
    }
  }

  async function handleChangeRole(memberId: string, newRole: string) {
    if (!organization) return;

    try {
      await patch(`/organizations/${organization.id}/members/${memberId}`, { role: newRole });
      // Update local state
      setMembers(members.map((m) =>
        m.id === memberId ? { ...m, role: newRole } : m
      ));
    } catch (err: any) {
      alert(err.message || 'Failed to change role');
    }
  }

  async function handleRemoveMember(memberId: string, memberEmail: string) {
    if (!organization) return;
    if (!confirm(`Are you sure you want to remove ${memberEmail} from the organization?`)) return;

    try {
      await deleteApi(`/organizations/${organization.id}/members/${memberId}`);
      setMembers(members.filter((m) => m.id !== memberId));
      if (orgDetails) {
        setOrgDetails({ ...orgDetails, seatCount: Math.max(orgDetails.seatCount - 1, 0) });
      }
    } catch (err: any) {
      alert(err.message || 'Failed to remove member');
    }
  }

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';
  const totalSeatsUsed = (orgDetails?.seatCount || 0) + invitations.length;
  const seatsAvailable = (orgDetails?.maxSeats || 5) - totalSeatsUsed;

  if (!organization) {
    return (
      <div className="text-center py-12">
        <UsersIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
        <h2 className="text-xl font-medium text-white mb-2">Organization Required</h2>
        <p className="text-slate-400">
          Create or join an organization to manage members.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/settings" className="text-slate-400 hover:text-white">Settings</Link>
        <span className="mx-2 text-slate-600">/</span>
        <span className="text-white">Members</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Organization Members</h1>
          <p className="mt-1 text-slate-400">
            Manage members in {organization.name}
          </p>
        </div>
        {canManageMembers && (
          <button
            onClick={() => setShowInviteModal(true)}
            disabled={seatsAvailable <= 0}
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Invite Member
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Seat usage */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-slate-300">Seat Usage</h3>
            <p className="mt-1 text-2xl font-bold text-white">
              {orgDetails?.seatCount || 0} / {orgDetails?.maxSeats || 5}
            </p>
            {invitations.length > 0 && (
              <p className="text-sm text-slate-400">
                + {invitations.length} pending invitation{invitations.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${Math.min(100, (totalSeatsUsed / (orgDetails?.maxSeats || 5)) * 100)}%` }}
            />
          </div>
        </div>
        {seatsAvailable <= 0 && (
          <p className="mt-2 text-sm text-amber-400">
            No seats available. Remove members or upgrade your plan to invite more.
          </p>
        )}
      </div>

      {/* Current members */}
      <section className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h2 className="text-lg font-medium text-white">
            Members ({members.length})
          </h2>
        </div>
        <div className="divide-y divide-slate-700">
          {members.map((member) => (
            <div key={member.id} className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {member.avatarUrl ? (
                  <img
                    src={member.avatarUrl}
                    alt={member.name || 'Member'}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                    <span className="text-slate-300 text-sm font-medium">
                      {(member.name || member.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-white font-medium">
                    {member.name || member.email}
                    {member.email === user?.primaryEmailAddress?.emailAddress && (
                      <span className="ml-2 text-xs text-slate-400">(you)</span>
                    )}
                  </p>
                  <p className="text-sm text-slate-400">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Role dropdown for owners/admins (can't change owner role or own role) */}
                {canManageMembers && member.role !== 'owner' && member.email !== user?.primaryEmailAddress?.emailAddress ? (
                  <select
                    value={member.role}
                    onChange={(e) => handleChangeRole(member.id, e.target.value)}
                    className="text-xs px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:ring-1 focus:ring-green-500"
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="member">Member</option>
                  </select>
                ) : (
                  <RoleBadge role={member.role} />
                )}
                <span className="text-xs text-slate-500">
                  Joined {new Date(member.joinedAt).toLocaleDateString()}
                </span>
                {/* Remove button for owners/admins (can't remove owner or self) */}
                {canManageMembers && member.role !== 'owner' && member.email !== user?.primaryEmailAddress?.emailAddress && (
                  <button
                    onClick={() => handleRemoveMember(member.id, member.email)}
                    className="p-1 text-slate-400 hover:text-red-400 rounded"
                    title="Remove member"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pending invitations */}
      {canManageMembers && invitations.length > 0 && (
        <section className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="text-lg font-medium text-white">
              Pending Invitations ({invitations.length})
            </h2>
          </div>
          <div className="divide-y divide-slate-700">
            {invitations.map((invitation) => (
              <div key={invitation.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                    <MailIcon className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{invitation.email}</p>
                    <p className="text-sm text-slate-400">
                      Invited {new Date(invitation.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <RoleBadge role={invitation.role} />
                  <span className="text-xs px-2 py-1 bg-amber-900/50 text-amber-300 rounded-full">
                    Pending
                  </span>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleResendInvitation(invitation.id)}
                      className="p-1 text-slate-400 hover:text-green-400 rounded"
                      title="Resend invitation"
                    >
                      <RefreshIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRevokeInvitation(invitation.id)}
                      className="p-1 text-slate-400 hover:text-red-400 rounded"
                      title="Revoke invitation"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Invite Team Member</h2>
            <form onSubmit={handleInvite}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="inviteEmail" className="block text-sm font-medium text-slate-300 mb-1">
                    Email Address *
                  </label>
                  <input
                    id="inviteEmail"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="colleague@company.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="inviteRole" className="block text-sm font-medium text-slate-300 mb-1">
                    Role
                  </label>
                  <select
                    id="inviteRole"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-400">
                    Admins can manage members and organization settings.
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-slate-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting || !inviteEmail.trim()}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md disabled:opacity-50"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Role badge component
function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    owner: 'bg-purple-900/50 text-purple-300',
    admin: 'bg-blue-900/50 text-blue-300',
    member: 'bg-slate-700 text-slate-300',
  };

  return (
    <span className={`text-xs px-2 py-1 rounded-full capitalize ${colors[role] || colors.member}`}>
      {role}
    </span>
  );
}

// Icons
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
